import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import {readUrlBackground} from "libs/bgNumbering";
import {useSearchParams} from "react-router-dom";

import {useChannelFromParams} from "hooks/useChannelFromParams";
import {useContentTypeFromParams} from "hooks/useContentTypeFromParams";
import {useProductFromParams} from "hooks/useProductFromParams";
import {
  CONTENT_TYPES,
  findChannelObj,
  isProductValidForChannel,
  defaultProductForChannel,
} from "data/products";
import {homeScope} from "data/offering";
import {pinnedSceneBg} from "libs/sceneBgSource";
import {withUnmarkedPresets} from "libs/sceneScope";
import {setAnalyticsContext} from "libs/analytics";
import {nextPixelationDirection} from "libs/pixelation";
import {FULL_MASK} from "libs/pixelationMask";
import {addedTemplateIds, extraTemplateListIds} from "libs/bannerComposition";
import {
  getGdnColorStateKey,
  nextGdnColorVariant,
  normalizeGdnAccentColor,
  normalizeGdnColorVariants,
} from "libs/gdnColorCycle";

// Single source of truth for the channel/layout/font/etc. scene slice.
// Same architecture as src/contexts/edits.js and src/contexts/images.js —
// adding a field is a one-place change here:
//   - scalar that round-trips     →  add to INITIAL_STATE + SCENE_FIELDS + SET_FIELDS
//   - scalar that doesn't         →  add to INITIAL_STATE + SET_FIELDS only (transient)
//   - list                        →  add to INITIAL_STATE + LIST_FIELDS
//
// The shape used to live in five hand-maintained lists (state hooks, reset,
// loadScene, App.buildScene's body, App.buildScene's dep array). Adding a
// field meant editing all five; missing one silently lost data on save/load.
// SETTING_SCENE_FIELDS below is the authoritative list — buildScene iterates
// it and loadScene consumes it, so they cannot drift.
export const INITIAL_SETTING_STATE = Object.freeze({
  isControlledBanner: false,
  // Resolved from URL params on mount; lazy-init in the provider below.
  channel: null,
  product: null,
  type: null,
  bgOption: "1",
  // Scene scopes (libs/sceneScope.ts): the per-banner, per-dimension mode
  // table, and the per-template layout, variant and base-colour records it
  // governs. `bases` holds the hex a banner set apart on its look; the rest
  // follow the scene's shared `base`.
  templateModes: {},
  layouts: {},
  variants: {},
  bases: {},
  // Which banners the sidebar edits: every banner, or the selected ones.
  // Transient — a selection is a working state, not part of a scene.
  scopeMode: "all",
  selectedTemplates: [],
  // The per-template values the applied default scene carried, per
  // dimension — what "reset to default" returns a banner to. Transient.
  defaultTemplateValues: {},
  layoutOption: 1,
  // A second design axis next to the layout, for products whose banner ships
  // in several brand treatments (JetBrains Air: Modul title / wordmark /
  // logo mark). 1 everywhere else; see `variantsForProduct` in data/products.
  variantOption: 1,
  fontOption: "en",

  // String identifier — must NOT be cast to Number on load. Consumers
  // (IconSelector, YouTubeThumbnail, EmailBanner) compare it with ===
  // against entries from ICONS = ["key", "book", "diamond", "java",
  // "track", "lamp"] and build URLs as `${UI}/Icons/${iconOption}.svg`.
  // Casting "key" to Number produces NaN; consumer URLs become
  // `/Icons/NaN.svg` and the IconSelector === check always fails.
  iconOption: "key",

  // Lists
  order: [],
  locked: [],
  visible: [],
  // The page's banners as an admin set them for a period (libs/bannerComposition.ts):
  // banners removed from the page, and on releases the banners of other sets
  // added to it. Scene fields, so default scenes and saved scenes carry them.
  hiddenTemplates: [],
  extraTemplates: [],
  // A saved scene's designs: the member drawn in each slot with two or more
  // designs (data/templateSlots.ts). A page without a saved scene does not
  // read it: it draws the live choice.
  templateMembers: [],

  // Scene-roundtripped scalars
  isUppercase: false,
  isFontToggle: false,
  isButtonEnabled: false,
  // Shared GDN control colors. Keys are "button" and "tag-0" through
  // "tag-3". One change updates that control in every GDN format.
  // Missing keys keep the original styling.
  gdnColorVariants: {},
  // Optional GDN-only replacement for the selected product accent. It applies
  // to product-color controls and CMD/Ctrl+Shift+2 rich-text formatting.
  gdnAccentColor: null,
  base: "000",
  generativeGradients: null,
  generativeSpeed: 10000,
  generativeCount: 0,
  generativeStops: 3,
  regenerateKey: 0,
  titleFontSize: "default",

  // Releases pixelation/mosaic overlay (Splash family). Applied to the
  // background image only — never to text, logos, or uploaded photos.
  pixelationEnabled: false,
  // "background" (sample bg image) | "product" (brand palette). Each source
  // resolves to a default palette that the user can override (see
  // pixelationPalettes).
  pixelationSource: "background",
  pixelationSizeCoefficient: 1.0,
  pixelationOpacity: 100,
  pixelationCoverage: 55, // % of width/height, measured from the solid edge
  pixelationGradual: true,
  pixelationOpacityRamp: false,
  pixelationBlend: "normal",
  pixelationSeed: 1,
  // Per-square click counts, nested by template id then keyed "row:col":
  // { [templateId]: { "row:col": count } }. Each click cycles that square's
  // opacity through fixed 25% steps (0/25/50/75/100), overriding the generated
  // opacity/fade/ramp. Nested by template so a click edits only the banner it
  // landed on, not every template that shares the pattern.
  pixelationCells: {},
  // What a click on a square does: "opacity" cycles its transparency (default),
  // "color" cycles its fill through the active palette. Tiler-style mode toggle.
  pixelationClickAction: "opacity",
  // Per-square color-cycle click counts, same per-template shape as
  // pixelationCells: { [templateId]: { "row:col": count } }. Each click advances
  // the square one step through the palette; independent of pixelationCells so a
  // square can carry both an opacity and a color override.
  pixelationColors: {},
  // Per-source palette overrides, keyed by source ("background" | "product").
  // An absent/empty entry means "use that source's resolved default" (sampled
  // bg colors / brand palette); a present array is the user's edited palette
  // for that source. Each source's edits persist independently across switches;
  // the Reset button clears this map so both revert to defaults.
  pixelationPalettes: {},
  // Per-template base tile size overrides, keyed by banner/template id → px.
  // Lets an unchained (locked) template pin its own mosaic square size instead
  // of following the title-font-derived size (title font × coefficient). An
  // absent id means "follow the title-derived size"; a present value is the
  // pinned base tile size in px.
  pixelationSizeOverrides: {},
  // Per-template coverage overrides, keyed by banner/template id → % (5–100).
  // Lets an unchained (locked) template pin its own dissolve-band extent instead
  // of following the global pixelationCoverage. An absent id means "follow the
  // global coverage"; a present value is the pinned coverage %. Honoured only
  // while the template is locked (parity with pixelationSizeOverrides).
  pixelationCoverageOverrides: {},
  // Per-template mosaic fade direction, keyed by banner/template id →
  // "ltr" | "ttb" | "rtl" | "btt". Every template defaults to "ltr" (mosaic
  // grows from the left, dissolving rightward); an unchained (locked) template
  // can rotate its own direction 90° at a time via the rotate control. Only
  // honoured while the template is locked (parity with pixelationSizeOverrides);
  // an absent id means "ltr". The default "ltr" is never stored — the rotate
  // action clears the entry when it wraps back around.
  pixelationDirectionOverrides: {},
  // The edge the mosaic grows from, for every template: "ltr" (from the
  // left), "ttb" (top), "rtl" (right) or "btt" (bottom). A locked template's
  // own direction (pixelationDirectionOverrides) wins over it.
  pixelationDirection: "ltr",
  // Squares: the tile shape — "square", "circle" (all corners round) or
  // "mixed" (each corner round by chance, libs/pixelation.ts).
  pixelationTileShape: "square",
  // Squares, mixed: the chance (%) that one corner of a tile is round.
  pixelationTileRoundness: 50,
  // Squares: the radius of a round corner, % of the tile's short side (50 =
  // circle when all corners are round, 100 = quarter disc on one corner).
  pixelationTileRadius: 50,
  // Per-template corner masks of clicked tiles, same shape as pixelationCells:
  // { [templateId]: { "row:col": corners } } (CORNER_* bits).
  pixelationShapes: {},
  // The shape of the overlay: "squares" (the edge mosaic above) or "dots" (a
  // halftone of round dots over the whole banner, libs/pixelationDots.ts).
  pixelationShape: "squares",
  // Dots: "image" sizes each dot by the background tone under it, "random"
  // by seeded noise (random structures of dots of different sizes).
  pixelationDotPattern: "image",
  // Dots: grid pitch = the title font size × this coefficient (as for squares,
  // so the @2x variants get a 2× grid).
  pixelationDotSizeCoefficient: 0.2,
  // Dots: the largest dot diameter, % of the grid cell (100 = dots touch).
  pixelationDotScale: 100,
  // Dots: tone curve, −100 (smaller dots) … 100 (larger dots).
  pixelationDotTone: 0,
  // Dots, image pattern: dark areas give the large dots.
  pixelationDotInvert: false,
  // Dots: "image" gives each dot the colour of the background under it,
  // "palette" takes the colours of the palette.
  pixelationDotColor: "image",
  // Dots, random pattern: the size of the random structures, in cells.
  pixelationDotStructure: 8,
  // Dots: what fills the area behind the dots where the mask shows them.
  // "base" = the banner's base colour (the image turns into dots), "none" =
  // the background image stays under the dots, or a "#rrggbb" colour.
  pixelationDotBackdrop: "base",
  // Per-template brush masks: { [templateId]: {base, strokes} } (see
  // libs/pixelationMask.ts). An absent id means the effect shows everywhere.
  pixelationMasks: {},

  // Transient state — not part of saved scenes
  // The pixelation brush: "off", "show" (paint the effect in) or "hide"
  // (erase it). Size is the diameter in layout px of a 1× banner; softness
  // is 0–100 %.
  pixelationBrush: "off",
  pixelationBrushSize: 80,
  pixelationBrushSoftness: 30,
  // The count of RESETs. The state that a hook derives from the route key
  // (useInitialScene) must start again after a RESET, also when the key is the
  // same (the Home button on the default landing).
  resetEpoch: 0,
  hash: "",
  // The image the loaded scene pins for its background (libs/sceneBgSource.ts).
  pinnedSceneBg: null,
  sceneSet: null,
  isDropzoneVisible: true,
  isExportLoading: false,
  isVideoLoading: false,
  isGifLoading: false,
  isBgToolsExpanded: {},
  isImageToolsExpanded: false,
  currentImageToolsId: null,
  isTypografEnabled: false,
  layoutAmount: 7,
  videoDuration: "5",
  videoExportCounter: {video: 0, gif: 0, pregif: 0},
  currentVideoPopup: "",
  lastDetachedId: null,
  isAnimationPaused: false,
  // Static frame captured only for a server-render export scene. Normal scene
  // saves omit it, so opening a saved generative scene still animates.
  generativeExportFrame: null,
  perProductBg: {},
});

// Authoritative roundtrip set — used so buildScene and loadScene agree
// by construction, not by hand-maintained lists.
export const SETTING_SCENE_FIELDS = Object.freeze([
  "product",
  "channel",
  "type",
  "bgOption",
  "layoutOption",
  "variantOption",
  "templateModes",
  "layouts",
  "variants",
  "bases",
  "fontOption",
  "iconOption",
  "isUppercase",
  "isFontToggle",
  "isButtonEnabled",
  "gdnColorVariants",
  "gdnAccentColor",
  "base",
  "locked",
  "visible",
  "order",
  "hiddenTemplates",
  "extraTemplates",
  "templateMembers",
  "generativeGradients",
  "generativeSpeed",
  "generativeCount",
  "generativeStops",
  "regenerateKey",
  "titleFontSize",
  "pixelationEnabled",
  "pixelationSource",
  "pixelationSizeCoefficient",
  "pixelationOpacity",
  "pixelationCoverage",
  "pixelationGradual",
  "pixelationOpacityRamp",
  "pixelationBlend",
  "pixelationSeed",
  "pixelationCells",
  "pixelationClickAction",
  "pixelationColors",
  "pixelationPalettes",
  "pixelationSizeOverrides",
  "pixelationCoverageOverrides",
  "pixelationDirectionOverrides",
  "pixelationShape",
  "pixelationDotPattern",
  "pixelationDotSizeCoefficient",
  "pixelationDotScale",
  "pixelationDotTone",
  "pixelationDotInvert",
  "pixelationDotColor",
  "pixelationDotStructure",
  "pixelationDotBackdrop",
  "pixelationMasks",
  "pixelationTileShape",
  "pixelationTileRoundness",
  "pixelationTileRadius",
  "pixelationShapes",
  "pixelationDirection",
]);

// The setting fields LOAD_DEFAULT_SCENE_OPTIONS applies, in apply order.
export const DEFAULT_SCENE_OPTION_FIELDS = Object.freeze([
  "titleFontSize",
  "generativeCount",
  "generativeStops",
  "generativeSpeed",
  "regenerateKey",
  "generativeGradients",
]);

// Pixelation overlay fields. Part of SETTING_SCENE_FIELDS (so they round-trip
// through loadScene), but App.buildScene strips them from the saved payload
// when the effect is off — a disabled overlay shouldn't bloat every scene or
// pin defaults that a future change might want to move. `pixelationEnabled`
// stays out of this list: it's the gate buildScene checks, and persisting it
// is what tells load the rest are meaningful.
export const PIXELATION_SCENE_FIELDS = Object.freeze([
  "pixelationSource",
  "pixelationSizeCoefficient",
  "pixelationOpacity",
  "pixelationCoverage",
  "pixelationGradual",
  "pixelationOpacityRamp",
  "pixelationBlend",
  "pixelationSeed",
  "pixelationCells",
  "pixelationClickAction",
  "pixelationColors",
  "pixelationPalettes",
  "pixelationSizeOverrides",
  "pixelationCoverageOverrides",
  "pixelationDirectionOverrides",
  "pixelationShape",
  "pixelationDotPattern",
  "pixelationDotSizeCoefficient",
  "pixelationDotScale",
  "pixelationDotTone",
  "pixelationDotInvert",
  "pixelationDotColor",
  "pixelationDotStructure",
  "pixelationDotBackdrop",
  "pixelationMasks",
  "pixelationTileShape",
  "pixelationTileRoundness",
  "pixelationTileRadius",
  "pixelationShapes",
  "pixelationDirection",
]);

// The scalar knobs of the tile shapes and of the dots. LOAD_SCENE gives them
// their defaults when a scene does not have them.
const PIXELATION_SHAPE_FIELDS = Object.freeze([
  "pixelationDirection",
  "pixelationTileShape",
  "pixelationTileRoundness",
  "pixelationTileRadius",
  "pixelationShape",
  "pixelationDotPattern",
  "pixelationDotSizeCoefficient",
  "pixelationDotScale",
  "pixelationDotTone",
  "pixelationDotInvert",
  "pixelationDotColor",
  "pixelationDotStructure",
  "pixelationDotBackdrop",
]);

// The full pixelation field set (gate + knobs). The gate is deliberately kept
// out of PIXELATION_SCENE_FIELDS (see above), so prepend it here. Used by the
// LOAD_PIXELATION action, which applies default-scene pixelation state.
export const PIXELATION_LOAD_FIELDS = Object.freeze([
  "pixelationEnabled",
  ...PIXELATION_SCENE_FIELDS,
]);

// Strip the pixelation knob fields from a scene payload about to be saved
// when the overlay is off, keeping the gate flag itself. The explicit
// `pixelationEnabled: false` must survive: LOAD_SCENE treats absence as
// off-because-the-scene-predates-the-feature, so deleting the flag would
// make "saved off" indistinguishable from "never had the feature". The
// knobs are meaningless without the overlay — dropping them keeps disabled
// scenes from carrying dead weight.
export function prunePixelationForSave(scene) {
  if (!scene.pixelationEnabled) {
    for (const f of PIXELATION_SCENE_FIELDS) delete scene[f];
  }
  return scene;
}

// Whitelist for SET_FIELD writes. Excludes lists (use LIST_REPLACE),
// videoExportCounter (use SET_VIDEO_EXPORT_COUNTER), perProductBg
// (use SET_PER_PRODUCT_BG), and isBgToolsExpanded (use SET_BG_TOOLS_EXPANDED).
const SET_FIELDS = new Set([
  "channel",
  "product",
  "type",
  "bgOption",
  "layoutOption",
  "variantOption",
  "templateModes",
  "layouts",
  "variants",
  "bases",
  "scopeMode",
  "selectedTemplates",
  "defaultTemplateValues",
  "fontOption",
  "iconOption",
  "isUppercase",
  "isFontToggle",
  "isButtonEnabled",
  "gdnAccentColor",
  "isTypografEnabled",
  "base",
  "generativeGradients",
  "generativeSpeed",
  "generativeCount",
  "generativeStops",
  "regenerateKey",
  "hash",
  "pinnedSceneBg",
  "sceneSet",
  "isDropzoneVisible",
  "isExportLoading",
  "isVideoLoading",
  "isGifLoading",
  "isImageToolsExpanded",
  "currentImageToolsId",
  "layoutAmount",
  "videoDuration",
  "currentVideoPopup",
  "lastDetachedId",
  "isAnimationPaused",
  "titleFontSize",
  "pixelationEnabled",
  "pixelationSource",
  "pixelationSizeCoefficient",
  "pixelationOpacity",
  "pixelationCoverage",
  "pixelationGradual",
  "pixelationOpacityRamp",
  "pixelationBlend",
  "pixelationSeed",
  "pixelationClickAction",
  "pixelationDirection",
  "pixelationTileShape",
  "pixelationTileRoundness",
  "pixelationTileRadius",
  "pixelationShape",
  "pixelationDotPattern",
  "pixelationDotSizeCoefficient",
  "pixelationDotScale",
  "pixelationDotTone",
  "pixelationDotInvert",
  "pixelationDotColor",
  "pixelationDotStructure",
  "pixelationDotBackdrop",
  "pixelationBrush",
  "pixelationBrushSize",
  "pixelationBrushSoftness",
]);

const LIST_FIELDS = new Set(["locked", "visible", "order", "hiddenTemplates"]);

// channel/product/type are deliberately preserved across RESET — useScene()
// sets them from scene data, and the URL params hooks provide initial values
// on mount. Resetting them here would clobber both.
const RESET_PRESERVED_FIELDS = ["channel", "product", "type"];

// The pixelation/mosaic overlay is OFF by default on every channel, releases
// included. Only an explicit `pixelationEnabled` from the applied default
// scene turns it on (LOAD_PIXELATION, which is present-wins) — a channel-wide
// default would silently overrule admins who never asked for the overlay, and
// there is no way to express "off" that survives it.

export function settingReducer(state, action) {
  switch (action.type) {
    case "RESET": {
      const next = {...INITIAL_SETTING_STATE};
      for (const f of RESET_PRESERVED_FIELDS) next[f] = state[f];
      next.resetEpoch = state.resetEpoch + 1;
      return next;
    }

    case "LOAD_SCENE": {
      const {sceneId, data} = action;
      const next = {...state, hash: sceneId};
      // Per scene, so a previous scene's pin cannot leak into this one.
      next.pinnedSceneBg = pinnedSceneBg(sceneId, data);

      if (data.isUppercase !== undefined) next.isUppercase = data.isUppercase;
      if (data.product !== undefined) next.product = data.product;
      if (data.channel !== undefined) next.channel = data.channel;
      if (data.type !== undefined) next.type = data.type;
      if (data.bgOption !== undefined) next.bgOption = data.bgOption;
      if (data.layoutOption !== undefined) next.layoutOption = +data.layoutOption;
      if (data.variantOption !== undefined) next.variantOption = +data.variantOption;
      // Scope records: {} when absent, so a previous scene's table cannot leak.
      next.templateModes = withUnmarkedPresets(
        data.templateModes && typeof data.templateModes === "object" ? data.templateModes : {},
        data
      );
      next.layouts = data.layouts && typeof data.layouts === "object" ? data.layouts : {};
      next.variants = data.variants && typeof data.variants === "object" ? data.variants : {};
      next.bases = data.bases && typeof data.bases === "object" ? data.bases : {};
      if (data.fontOption !== undefined) next.fontOption = data.fontOption;
      if (data.isFontToggle !== undefined) next.isFontToggle = data.isFontToggle;
      next.isButtonEnabled = data.isButtonEnabled === true;
      next.gdnColorVariants = normalizeGdnColorVariants(data.gdnColorVariants);
      next.gdnAccentColor = normalizeGdnAccentColor(data.gdnAccentColor);
      if (data.titleFontSize !== undefined) next.titleFontSize = data.titleFontSize;

      // No Number cast — see iconOption comment in INITIAL_SETTING_STATE.
      // Casting "key" produces NaN, which breaks every downstream consumer.
      if (data.iconOption !== undefined) next.iconOption = data.iconOption;

      if (data.locked !== undefined) {
        next.locked = data.locked;
        if (Array.isArray(data.locked) && data.locked.length) {
          next.lastDetachedId = data.locked[data.locked.length - 1];
        }
      }
      if (data.visible !== undefined) next.visible = data.visible;
      if (data.base !== undefined) next.base = data.base;
      if (data.order !== undefined) next.order = data.order;
      // The page's banners: [] when absent, so a scene saved before them, or
      // the previous scene's list, cannot remove or add banners here.
      next.hiddenTemplates = Array.isArray(data.hiddenTemplates) ? data.hiddenTemplates : [];
      next.extraTemplates = Array.isArray(data.extraTemplates) ? data.extraTemplates : [];
      // [] when absent: a scene saved before a slot had a second design drew
      // the slot's first design (data/templateSlots.ts).
      next.templateMembers = Array.isArray(data.templateMembers) ? data.templateMembers : [];

      // Restore generative animation parameters. Order matters: numeric
      // params (+ regenerateKey) before generativeGradients so that when
      // GenerativeBg mounts and computes its paletteKey, the loaded
      // gradients match and regeneration is skipped (see GenerativeBg
      // useEffect cache guard).
      if (data.generativeCount !== undefined) next.generativeCount = data.generativeCount;
      if (data.generativeStops !== undefined) next.generativeStops = data.generativeStops;
      if (data.generativeSpeed !== undefined) next.generativeSpeed = data.generativeSpeed;
      if (data.regenerateKey !== undefined) next.regenerateKey = data.regenerateKey;
      if (data.generativeGradients !== undefined) {
        next.generativeGradients = data.generativeGradients;
      }
      next.generativeExportFrame = data.generativeExportFrame ?? null;

      // Pixelation overlay. Absence is authoritative and means OFF: the
      // scene either predates the pixelation feature or was saved with the
      // overlay off (buildScene persists the gate flag explicitly — see
      // prunePixelationForSave). Forcing a boolean also clears any stale
      // `true` from whatever scene was on screen before (loadScene has no
      // preceding RESET). The remaining knobs are restored only when present.
      next.pixelationEnabled =
        data.pixelationEnabled !== undefined ? data.pixelationEnabled : false;
      if (data.pixelationSource !== undefined) next.pixelationSource = data.pixelationSource;
      if (data.pixelationSizeCoefficient !== undefined)
        next.pixelationSizeCoefficient = data.pixelationSizeCoefficient;
      // Backwards compat: load old pixelationSize as coefficient (approximate)
      else if (data.pixelationSize !== undefined)
        next.pixelationSizeCoefficient = data.pixelationSize / 80;
      if (data.pixelationOpacity !== undefined) next.pixelationOpacity = data.pixelationOpacity;
      if (data.pixelationCoverage !== undefined) next.pixelationCoverage = data.pixelationCoverage;
      if (data.pixelationGradual !== undefined) next.pixelationGradual = data.pixelationGradual;
      if (data.pixelationOpacityRamp !== undefined)
        next.pixelationOpacityRamp = data.pixelationOpacityRamp;
      if (data.pixelationBlend !== undefined) next.pixelationBlend = data.pixelationBlend;
      if (data.pixelationSeed !== undefined) next.pixelationSeed = data.pixelationSeed;
      // Per-square click counts. Force to {} when absent (same authoritative-
      // absence reasoning as pixelationEnabled): no preceding RESET, so a stale
      // map from the previous scene must not leak onto a freshly loaded one.
      next.pixelationCells =
        data.pixelationCells && typeof data.pixelationCells === "object"
          ? data.pixelationCells
          : {};
      // Click action + per-square color clicks. Same authoritative-absence
      // reset as the other pixelation fields (no preceding RESET on load).
      next.pixelationClickAction =
        data.pixelationClickAction !== undefined ? data.pixelationClickAction : "opacity";
      next.pixelationColors =
        data.pixelationColors && typeof data.pixelationColors === "object"
          ? data.pixelationColors
          : {};
      // Per-source palette overrides. Force to {} when absent so a stale map
      // from the previous scene can't leak (no preceding RESET on load).
      next.pixelationPalettes =
        data.pixelationPalettes && typeof data.pixelationPalettes === "object"
          ? data.pixelationPalettes
          : {};
      // Per-template base tile size overrides. Force to {} when absent so a
      // stale map from the previous scene can't leak (no preceding RESET on
      // load).
      next.pixelationSizeOverrides =
        data.pixelationSizeOverrides && typeof data.pixelationSizeOverrides === "object"
          ? data.pixelationSizeOverrides
          : {};
      // Per-template coverage overrides. Force to {} when absent so a stale map
      // from the previous scene can't leak (no preceding RESET on load).
      next.pixelationCoverageOverrides =
        data.pixelationCoverageOverrides && typeof data.pixelationCoverageOverrides === "object"
          ? data.pixelationCoverageOverrides
          : {};
      // Per-template fade direction overrides. Force to {} when absent so a
      // stale map from the previous scene can't leak (no preceding RESET on
      // load).
      next.pixelationDirectionOverrides =
        data.pixelationDirectionOverrides && typeof data.pixelationDirectionOverrides === "object"
          ? data.pixelationDirectionOverrides
          : {};
      // Tile shapes, dots and masks. Absence gives the defaults, as for the
      // click action: a scene from before them must not show the shapes of
      // the scene that was on screen before it.
      for (const f of PIXELATION_SHAPE_FIELDS) {
        next[f] = data[f] !== undefined ? data[f] : INITIAL_SETTING_STATE[f];
      }
      next.pixelationMasks =
        data.pixelationMasks && typeof data.pixelationMasks === "object"
          ? data.pixelationMasks
          : {};
      next.pixelationShapes =
        data.pixelationShapes && typeof data.pixelationShapes === "object"
          ? data.pixelationShapes
          : {};

      // sceneSet is always overwritten — including with `null` if absent.
      // Preserves pre-refactor behavior; consumers expect a clear-on-load.
      next.sceneSet = data.set || null;

      // A saved scene can carry a product its channel no longer offers
      // (e.g. a livestream scene saved as IJ before livestream narrowed to
      // JB). Correct it here so the first post-load render is final —
      // consumers key fetches on (channel, product), and a transitional
      // invalid product costs a wasted request per consumer (guarded by
      // tests/channel-gate.spec.cjs).
      if (!isProductValidForChannel(next.product?.value, next.channel?.value)) {
        next.product = defaultProductForChannel(next.channel?.value);
      }

      return next;
    }

    case "SET_FIELD": {
      if (!SET_FIELDS.has(action.field)) {
        throw new Error(`SET_FIELD: unknown or protected field "${action.field}"`);
      }
      const value =
        action.field === "gdnAccentColor" ? normalizeGdnAccentColor(action.value) : action.value;
      const next = {...state, [action.field]: value};
      // Product validity is channel-scoped — changing channel re-validates
      // it (same invariant as LOAD_SCENE above).
      if (
        action.field === "channel" &&
        !isProductValidForChannel(next.product?.value, next.channel?.value)
      ) {
        next.product = defaultProductForChannel(next.channel?.value);
      }
      return next;
    }

    case "LOAD_PIXELATION": {
      // Default-scene apply path (useInitialScene). Unlike LOAD_SCENE, this
      // does NOT force defaults on absence: there's no preceding RESET here,
      // so a default scene that says nothing about pixelation must leave the
      // state on screen alone. So it's "present wins" for the gate + every
      // knob — matching how useInitialScene applies every other field. Since
      // nothing defaults the overlay on (see RESET above), an explicit
      // `pixelationEnabled: true` from a default scene is the only thing that
      // turns it on. A default saved with the overlay off carries an explicit
      // `pixelationEnabled: false` (buildScene keeps the gate; see
      // prunePixelationForSave), which is what makes "saved off" stick. (COART-1465)
      const {data} = action;
      if (!data || typeof data !== "object") return state;
      const next = {...state};
      for (const f of PIXELATION_LOAD_FIELDS) {
        if (data[f] !== undefined) next[f] = data[f];
      }
      return next;
    }

    case "LOAD_DEFAULT_SCENE_OPTIONS": {
      // Default-scene apply path (useInitialScene), "present wins" like
      // LOAD_PIXELATION: the title size and the generative background. The
      // generative order is the one LOAD_SCENE uses — the numeric params and
      // regenerateKey before the gradients, so GenerativeBg finds a matching
      // paletteKey and does not regenerate them.
      const {data} = action;
      if (!data || typeof data !== "object") return state;
      const next = {...state};
      for (const f of DEFAULT_SCENE_OPTION_FIELDS) {
        if (data[f] !== undefined) next[f] = data[f];
      }
      return next;
    }

    case "LIST_REPLACE": {
      if (!LIST_FIELDS.has(action.field)) {
        throw new Error(`LIST_REPLACE: unknown list field "${action.field}"`);
      }
      // loadLocked side-effect: setting lastDetachedId from the last entry.
      const next = {...state, [action.field]: action.value};
      if (action.field === "locked" && Array.isArray(action.value) && action.value.length) {
        next.lastDetachedId = action.value[action.value.length - 1];
      }
      return next;
    }

    case "ADD_LOCKED":
      // addLocked also bumps lastDetachedId — the toggle that creates a
      // detached banner records who was last detached so other UI can
      // highlight it.
      return {
        ...state,
        locked: [...state.locked, action.value],
        lastDetachedId: action.value,
      };

    case "REMOVE_LOCKED":
      return {
        ...state,
        locked: state.locked.filter((item) => item !== action.value),
      };

    case "ADD_VISIBLE":
      return {...state, visible: [...state.visible, action.value]};

    case "REMOVE_VISIBLE":
      return {
        ...state,
        visible: state.visible.filter((item) => item !== action.value),
      };

    case "RESET_VISIBLE":
      return {...state, visible: []};

    case "SET_EXTRA_TEMPLATES": {
      // A banner added from another set shows at once: the ids it takes in the
      // lists (libs/bannerComposition.ts) join the visible list, and the sort
      // order after the banners in it. Only the banners that `value` adds are
      // touched, so a user's eye on a banner that was there stays.
      const {value, scope} = action;
      const next = {...state, extraTemplates: value};
      const listIds = extraTemplateListIds(addedTemplateIds(state.extraTemplates, value), scope);
      const visible = new Set(state.visible);
      const newVisible = listIds.filter((id) => !visible.has(id));
      if (newVisible.length > 0) next.visible = [...state.visible, ...newVisible];
      if (state.order.length > 0) {
        const ordered = new Set(state.order);
        const newOrder = listIds.filter((id) => !ordered.has(id));
        if (newOrder.length > 0) next.order = [...state.order, ...newOrder];
      }
      return next;
    }

    case "SET_BG_TOOLS_EXPANDED": {
      const {id, isExpanded} = action;
      const next = {...state.isBgToolsExpanded};
      // Falsy id ⇒ collapse all (used by editIsBgToolsExpanded(false)).
      if (!id && id !== 0) {
        for (const k of Object.keys(next)) next[k] = false;
        return {...state, isBgToolsExpanded: next};
      }
      // Exclusive mode on expand: collapse all others first.
      if (isExpanded) {
        for (const k of Object.keys(next)) next[k] = false;
      }
      next[id] = isExpanded;
      return {...state, isBgToolsExpanded: next};
    }

    case "SET_VIDEO_EXPORT_COUNTER":
      return {
        ...state,
        videoExportCounter: {...state.videoExportCounter, [action.key]: action.value},
      };

    case "TOGGLE_ANIMATION_PAUSED":
      return {...state, isAnimationPaused: !state.isAnimationPaused};

    case "SET_PER_PRODUCT_BG": {
      const next =
        typeof action.value === "function" ? action.value(state.perProductBg) : action.value;
      return {...state, perProductBg: next};
    }

    case "REGENERATE_GRADIENTS":
      return {
        ...state,
        generativeGradients: null,
        regenerateKey: state.regenerateKey + 1,
      };

    case "REGENERATE_PIXELATION":
      return {...state, pixelationSeed: state.pixelationSeed + 1};

    case "TOGGLE_PIXELATION_CELL": {
      // Each click on a square advances its click count by one. The draw layer
      // turns that count into an opacity step (0/25/50/75/100), cycling — so
      // the count grows monotonically and the modulo in overridePercent() maps
      // it back into the cycle. Stored per template: { [id]: { "row:col": n } },
      // so the click only affects the banner it landed on.
      const {id, key} = action;
      const tplCells = state.pixelationCells[id] || {};
      const prev = tplCells[key] || 0;
      return {
        ...state,
        pixelationCells: {...state.pixelationCells, [id]: {...tplCells, [key]: prev + 1}},
      };
    }

    case "CYCLE_PIXELATION_COLOR": {
      // Each click advances the square one step through the palette. The draw
      // layer adds this count to the cell's base color index (mod palette size),
      // so the count grows monotonically and wraps. Stored per template:
      // { [id]: { "row:col": n } }, so the click only affects the clicked banner.
      const {id, key} = action;
      const tplColors = state.pixelationColors[id] || {};
      const prev = tplColors[key] || 0;
      return {
        ...state,
        pixelationColors: {...state.pixelationColors, [id]: {...tplColors, [key]: prev + 1}},
      };
    }

    case "LOAD_GDN_COLOR_VARIANTS":
      return {...state, gdnColorVariants: normalizeGdnColorVariants(action.value)};

    case "CYCLE_GDN_COLOR": {
      const stateKey = getGdnColorStateKey(action.key);
      const current = state.gdnColorVariants[stateKey];
      return {
        ...state,
        gdnColorVariants: {
          ...state.gdnColorVariants,
          [stateKey]: nextGdnColorVariant(current),
        },
      };
    }

    case "CLEAR_GDN_COLOR": {
      const stateKey = getGdnColorStateKey(action.key);
      if (state.gdnColorVariants[stateKey] === undefined) return state;
      const gdnColorVariants = {...state.gdnColorVariants};
      delete gdnColorVariants[stateKey];
      return {...state, gdnColorVariants};
    }

    case "SET_PIXELATION_PALETTE": {
      // Store (or clear) the user's edited palette for one source. A non-empty
      // array overrides that source's resolved default; an empty array removes
      // the override so the default is used again. Other sources are untouched,
      // so each source's edits persist independently across switches.
      const {source, palette} = action;
      const next = {...state.pixelationPalettes};
      if (palette && palette.length) next[source] = palette;
      else delete next[source];
      return {...state, pixelationPalettes: next};
    }

    case "RESET_PIXELATION_PALETTES":
      // Reset button: drop every per-source override so all sources revert to
      // their resolved defaults (sampled bg colors / brand palette).
      return {...state, pixelationPalettes: {}};

    case "SET_PIXELATION_SIZE_OVERRIDE": {
      // Pin (or clear) one template's base tile size. A positive value pins it;
      // null/non-positive removes the override so the template follows the
      // title-font-derived size (title font × coefficient) again.
      const {id, value} = action;
      const next = {...state.pixelationSizeOverrides};
      // value > 0 also rejects null/undefined, so clearing is "any non-positive".
      if (value > 0) next[id] = value;
      else delete next[id];
      return {...state, pixelationSizeOverrides: next};
    }

    case "SET_PIXELATION_COVERAGE_OVERRIDE": {
      // Pin (or clear) one template's coverage %. A positive value pins it;
      // null/non-positive removes the override so the template follows the
      // global pixelationCoverage again.
      const {id, value} = action;
      const next = {...state.pixelationCoverageOverrides};
      if (value > 0) next[id] = value;
      else delete next[id];
      return {...state, pixelationCoverageOverrides: next};
    }

    case "RESET_PIXELATION_TEMPLATE": {
      // "Use defaults" in the per-template popover: drop this template's size,
      // coverage, and direction overrides in one step so it follows the global
      // mosaic settings again (direction back to pixelationDirection).
      const {id} = action;
      const size = {...state.pixelationSizeOverrides};
      const coverage = {...state.pixelationCoverageOverrides};
      const direction = {...state.pixelationDirectionOverrides};
      delete size[id];
      delete coverage[id];
      delete direction[id];
      return {
        ...state,
        pixelationSizeOverrides: size,
        pixelationCoverageOverrides: coverage,
        pixelationDirectionOverrides: direction,
      };
    }

    case "CYCLE_PIXELATION_DIRECTION": {
      // Rotate one template's mosaic 90° clockwise around the fixed order
      // (ltr → ttb → rtl → btt → ltr), starting from the global
      // pixelationDirection. The global direction is the default, so it's never
      // stored — wrapping back to it clears the entry, keeping the map (and
      // saved scenes) free of no-op defaults.
      const {id} = action;
      const current = state.pixelationDirectionOverrides[id] || state.pixelationDirection;
      const nextDir = nextPixelationDirection(current);
      const next = {...state.pixelationDirectionOverrides};
      if (nextDir === state.pixelationDirection) delete next[id];
      else next[id] = nextDir;
      return {...state, pixelationDirectionOverrides: next};
    }

    case "SET_PIXELATION_CELL_SHAPE": {
      // Set the round corners of one clicked tile (CORNER_* bits). Stored per
      // template, so the click only changes the banner it landed on.
      const {id, key, corners} = action;
      return {
        ...state,
        pixelationShapes: {
          ...state.pixelationShapes,
          [id]: {...state.pixelationShapes[id], [key]: corners},
        },
      };
    }

    case "RESET_PIXELATION_SHAPES":
      // The clicked tiles take the generated shape again.
      return {...state, pixelationShapes: {}};

    case "ADD_PIXELATION_MASK_STROKE": {
      // One finished brush stroke on one template. A template with no mask
      // starts from the full mask (the effect everywhere).
      const {id, stroke} = action;
      const prev = state.pixelationMasks[id] || FULL_MASK;
      return {
        ...state,
        pixelationMasks: {
          ...state.pixelationMasks,
          [id]: {base: prev.base, strokes: [...prev.strokes, stroke]},
        },
      };
    }

    case "UNDO_PIXELATION_MASK_STROKE": {
      const {id} = action;
      const prev = state.pixelationMasks[id];
      if (!prev || !prev.strokes.length) return state;
      return {
        ...state,
        pixelationMasks: {
          ...state.pixelationMasks,
          [id]: {base: prev.base, strokes: prev.strokes.slice(0, -1)},
        },
      };
    }

    case "FILL_PIXELATION_MASK": {
      // Start a template's mask again: value 1 shows the effect everywhere
      // (the same as no mask, so the entry goes), 0 hides it everywhere.
      const {id, value} = action;
      const next = {...state.pixelationMasks};
      if (value) delete next[id];
      else next[id] = {base: 0, strokes: []};
      return {...state, pixelationMasks: next};
    }

    case "RESET_PIXELATION_MASKS":
      return {...state, pixelationMasks: {}};

    default:
      return state;
  }
}

const NOOP = () => {};

export const Setting = createContext({
  ...INITIAL_SETTING_STATE,
  dataChannel: "",
  dataType: "",
  dataChannelObj: null,
  dataTypeObj: null,
  reset: NOOP,
  loadScene: NOOP,
  loadPixelation: NOOP,
  loadDefaultSceneOptions: NOOP,
  editChannel: NOOP,
  editProduct: NOOP,
  editType: NOOP,
  editBgOption: NOOP,
  editLayoutOption: NOOP,
  editVariantOption: NOOP,
  editTemplateModes: NOOP,
  editLayouts: NOOP,
  editVariants: NOOP,
  editBases: NOOP,
  editScopeMode: NOOP,
  editSelectedTemplates: NOOP,
  editDefaultTemplateValues: NOOP,
  editFontOption: NOOP,
  editIconOption: NOOP,
  editHash: NOOP,
  editPinnedSceneBg: NOOP,
  editBase: NOOP,
  editLayoutAmount: NOOP,
  editIsExportLoading: NOOP,
  editIsVideoLoading: NOOP,
  editIsGifLoading: NOOP,
  editIsImageToolsExpanded: NOOP,
  editCurrentImageToolsId: NOOP,
  editIsBgToolsExpanded: NOOP,
  editIsFontToggle: NOOP,
  editIsButtonEnabled: NOOP,
  editGdnAccentColor: NOOP,
  loadGdnColorVariants: NOOP,
  cycleGdnColor: NOOP,
  clearGdnColor: NOOP,
  editIsTypografEnabled: NOOP,
  editVideoDuration: NOOP,
  editCurrentVideoPopup: NOOP,
  editVideoExportCounter: NOOP,
  toggleUppercase: NOOP,
  makeDropzoneVisible: NOOP,
  resetVisible: NOOP,
  editHiddenTemplates: NOOP,
  editExtraTemplates: NOOP,
  addVisible: NOOP,
  addLocked: NOOP,
  removeLocked: NOOP,
  editLocked: NOOP,
  loadLocked: NOOP,
  loadVisible: NOOP,
  editVisible: NOOP,
  editOrder: NOOP,
  clearSceneSet: NOOP,
  editPerProductBg: NOOP,
  editGenerativeGradients: NOOP,
  toggleAnimationPaused: NOOP,
  editGenerativeSpeed: NOOP,
  editGenerativeCount: NOOP,
  editGenerativeStops: NOOP,
  regenerateGradients: NOOP,
  registerGenerativeFrameReader: () => NOOP,
  getGenerativeFrame: () => null,
  editTitleFontSize: NOOP,
  editPixelationEnabled: NOOP,
  editPixelationSource: NOOP,
  editPixelationSizeCoefficient: NOOP,
  editPixelationOpacity: NOOP,
  editPixelationCoverage: NOOP,
  editPixelationGradual: NOOP,
  editPixelationOpacityRamp: NOOP,
  editPixelationBlend: NOOP,
  editPixelationClickAction: NOOP,
  setPixelationPalette: NOOP,
  resetPixelationPalettes: NOOP,
  editPixelationSizeOverride: NOOP,
  editPixelationCoverageOverride: NOOP,
  cyclePixelationDirection: NOOP,
  resetPixelationTemplate: NOOP,
  regeneratePixelation: NOOP,
  togglePixelationCell: NOOP,
  cyclePixelationColor: NOOP,
  editPixelationShape: NOOP,
  editPixelationDotPattern: NOOP,
  editPixelationDotSizeCoefficient: NOOP,
  editPixelationDotScale: NOOP,
  editPixelationDotTone: NOOP,
  editPixelationDotInvert: NOOP,
  editPixelationDotColor: NOOP,
  editPixelationDotStructure: NOOP,
  editPixelationDotBackdrop: NOOP,
  editPixelationBrush: NOOP,
  editPixelationBrushSize: NOOP,
  editPixelationBrushSoftness: NOOP,
  setPixelationCellShape: NOOP,
  resetPixelationShapes: NOOP,
  editPixelationDirection: NOOP,
  editPixelationTileShape: NOOP,
  editPixelationTileRoundness: NOOP,
  editPixelationTileRadius: NOOP,
  addPixelationMaskStroke: NOOP,
  undoPixelationMaskStroke: NOOP,
  fillPixelationMask: NOOP,
  resetPixelationMasks: NOOP,
});

/**
 * The selector state that the URL query names. The provider reads it once,
 * when it mounts. Later URL changes reach the context through the controls
 * that write them.
 */
export function settingStateFromSearchParams(searchParams) {
  // A tile number (`?bg=`) is read when App knows the tiles of the page
  // (hooks/useBackgroundNumbers.ts); until then the "1" placeholder holds.
  const urlBackground = readUrlBackground(searchParams);
  return {
    bgOption: urlBackground?.kind === "identity" ? urlBackground.background : "1",
    layoutOption: searchParams.get("layout") ? Number(searchParams.get("layout")) : 1,
    variantOption: searchParams.get("variant") ? Number(searchParams.get("variant")) : 1,
    fontOption: searchParams.get("localization") ?? "en",
    // The Localization toggle and the language buttons write `localization`
    // only while the toggle is on, so the param also names the toggle.
    isFontToggle: Boolean(searchParams.get("localization")),
  };
}

export const SettingContextProvider = ({children}) => {
  const channelFromParams = useChannelFromParams();
  const productFromParams = useProductFromParams();
  const contentTypeFromParams = useContentTypeFromParams();
  const [searchParams] = useSearchParams();
  const generativeFrameReaderRef = useRef(null);

  // useReducer's lazy init runs once on mount, so URL-derived initial
  // values are captured exactly like the previous useState(initial) calls.
  // Subsequent URL changes flow through editChannel/editProduct/editType
  // from App.js's URL→context-sync useLayoutEffects (preserved behavior).
  const [state, dispatch] = useReducer(settingReducer, null, () => ({
    ...INITIAL_SETTING_STATE,
    channel: channelFromParams,
    product: productFromParams,
    type: contentTypeFromParams,
    ...settingStateFromSearchParams(searchParams),
  }));

  // Keep the analytics auto-enrichment context in sync with the current
  // selection. track() reads from this so callers don't have to pass
  // product/channel/contentType on every domain event.
  useEffect(() => {
    setAnalyticsContext({
      product: state.product?.value,
      channel: state.channel?.value,
      contentType: state.type?.value,
    });
  }, [state.product, state.channel, state.type]);

  // dispatch identity is stable across renders, so all wrappers below
  // memoized with [] deps are also stable. Consumer hooks rely on this
  // for useEffect-dep correctness.
  const reset = useCallback(() => dispatch({type: "RESET"}), []);
  const loadScene = useCallback(
    (sceneId, data) => dispatch({type: "LOAD_SCENE", sceneId, data}),
    []
  );
  // Apply just the pixelation subset of a scene (default-scene load path in
  // useInitialScene, which doesn't go through LOAD_SCENE). See COART-1465.
  const loadPixelation = useCallback((data) => dispatch({type: "LOAD_PIXELATION", data}), []);
  const loadDefaultSceneOptions = useCallback(
    (data) => dispatch({type: "LOAD_DEFAULT_SCENE_OPTIONS", data}),
    []
  );

  const setField = useCallback(
    (field) => (value) => dispatch({type: "SET_FIELD", field, value}),
    []
  );
  const listReplace = useCallback(
    (field) => (value) => dispatch({type: "LIST_REPLACE", field, value}),
    []
  );

  const wrappers = useMemo(
    () => ({
      // Routing / scene selectors
      editChannel: setField("channel"),
      editProduct: setField("product"),
      editType: setField("type"),
      editBgOption: setField("bgOption"),
      editLayoutOption: setField("layoutOption"),
      editVariantOption: setField("variantOption"),
      editTemplateModes: setField("templateModes"),
      editLayouts: setField("layouts"),
      editVariants: setField("variants"),
      editBases: setField("bases"),
      editScopeMode: setField("scopeMode"),
      editSelectedTemplates: setField("selectedTemplates"),
      editDefaultTemplateValues: setField("defaultTemplateValues"),
      editFontOption: setField("fontOption"),
      editIconOption: setField("iconOption"),
      // Scene scalars
      editBase: setField("base"),
      editIsFontToggle: setField("isFontToggle"),
      editIsButtonEnabled: setField("isButtonEnabled"),
      editGdnAccentColor: setField("gdnAccentColor"),
      editIsTypografEnabled: setField("isTypografEnabled"),
      toggleUppercase: setField("isUppercase"),
      editTitleFontSize: setField("titleFontSize"),
      // Generative
      editGenerativeGradients: setField("generativeGradients"),
      editGenerativeSpeed: setField("generativeSpeed"),
      editGenerativeCount: setField("generativeCount"),
      editGenerativeStops: setField("generativeStops"),
      // Pixelation overlay
      editPixelationEnabled: setField("pixelationEnabled"),
      editPixelationSource: setField("pixelationSource"),
      editPixelationSizeCoefficient: setField("pixelationSizeCoefficient"),
      editPixelationOpacity: setField("pixelationOpacity"),
      editPixelationCoverage: setField("pixelationCoverage"),
      editPixelationGradual: setField("pixelationGradual"),
      editPixelationOpacityRamp: setField("pixelationOpacityRamp"),
      editPixelationBlend: setField("pixelationBlend"),
      editPixelationClickAction: setField("pixelationClickAction"),
      editPixelationDirection: setField("pixelationDirection"),
      editPixelationTileShape: setField("pixelationTileShape"),
      editPixelationTileRoundness: setField("pixelationTileRoundness"),
      editPixelationTileRadius: setField("pixelationTileRadius"),
      editPixelationShape: setField("pixelationShape"),
      editPixelationDotPattern: setField("pixelationDotPattern"),
      editPixelationDotSizeCoefficient: setField("pixelationDotSizeCoefficient"),
      editPixelationDotScale: setField("pixelationDotScale"),
      editPixelationDotTone: setField("pixelationDotTone"),
      editPixelationDotInvert: setField("pixelationDotInvert"),
      editPixelationDotColor: setField("pixelationDotColor"),
      editPixelationDotStructure: setField("pixelationDotStructure"),
      editPixelationDotBackdrop: setField("pixelationDotBackdrop"),
      editPixelationBrush: setField("pixelationBrush"),
      editPixelationBrushSize: setField("pixelationBrushSize"),
      editPixelationBrushSoftness: setField("pixelationBrushSoftness"),
      // Transient UI
      editHash: setField("hash"),
      // A pick on a saved link pins the image of its tile (BgSelector).
      editPinnedSceneBg: setField("pinnedSceneBg"),
      makeDropzoneVisible: setField("isDropzoneVisible"),
      editIsExportLoading: setField("isExportLoading"),
      editIsVideoLoading: setField("isVideoLoading"),
      editIsGifLoading: setField("isGifLoading"),
      editIsImageToolsExpanded: setField("isImageToolsExpanded"),
      editCurrentImageToolsId: setField("currentImageToolsId"),
      editLayoutAmount: setField("layoutAmount"),
      editVideoDuration: setField("videoDuration"),
      editCurrentVideoPopup: setField("currentVideoPopup"),
      // Lists
      editOrder: listReplace("order"),
      editHiddenTemplates: listReplace("hiddenTemplates"),
      loadLocked: listReplace("locked"),
      loadVisible: listReplace("visible"),
    }),
    [setField, listReplace]
  );

  const addVisible = useCallback((value) => dispatch({type: "ADD_VISIBLE", value}), []);
  // `scope`: how the page lists its banners (libs/bannerComposition.ts BannerListScope).
  const editExtraTemplates = useCallback(
    (value, scope) => dispatch({type: "SET_EXTRA_TEMPLATES", value, scope}),
    []
  );
  const resetVisible = useCallback(() => dispatch({type: "RESET_VISIBLE"}), []);
  const editVisible = useCallback(
    (template, value) =>
      dispatch({type: value ? "ADD_VISIBLE" : "REMOVE_VISIBLE", value: template}),
    []
  );

  const addLocked = useCallback((value) => dispatch({type: "ADD_LOCKED", value}), []);
  const removeLocked = useCallback((value) => dispatch({type: "REMOVE_LOCKED", value}), []);
  const editLocked = useCallback(
    (template, value) => dispatch({type: value ? "ADD_LOCKED" : "REMOVE_LOCKED", value: template}),
    []
  );

  const editIsBgToolsExpanded = useCallback(
    (id, isExpanded) => dispatch({type: "SET_BG_TOOLS_EXPANDED", id, isExpanded}),
    []
  );
  const editVideoExportCounter = useCallback(
    (value, key) => dispatch({type: "SET_VIDEO_EXPORT_COUNTER", key, value}),
    []
  );
  const toggleAnimationPaused = useCallback(() => dispatch({type: "TOGGLE_ANIMATION_PAUSED"}), []);
  const editPerProductBg = useCallback(
    (value) => dispatch({type: "SET_PER_PRODUCT_BG", value}),
    []
  );
  const regenerateGradients = useCallback(() => dispatch({type: "REGENERATE_GRADIENTS"}), []);
  const registerGenerativeFrameReader = useCallback((reader) => {
    if (typeof reader !== "function") {
      throw new Error("Generative frame reader must be a function");
    }
    generativeFrameReaderRef.current = reader;
    return () => {
      if (generativeFrameReaderRef.current === reader) generativeFrameReaderRef.current = null;
    };
  }, []);
  const getGenerativeFrame = useCallback(() => {
    const reader = generativeFrameReaderRef.current;
    return reader ? reader() : null;
  }, []);
  const regeneratePixelation = useCallback(() => dispatch({type: "REGENERATE_PIXELATION"}), []);
  const loadGdnColorVariants = useCallback(
    (value) => dispatch({type: "LOAD_GDN_COLOR_VARIANTS", value}),
    []
  );
  const cycleGdnColor = useCallback((key) => dispatch({type: "CYCLE_GDN_COLOR", key}), []);
  const clearGdnColor = useCallback((key) => dispatch({type: "CLEAR_GDN_COLOR", key}), []);
  const togglePixelationCell = useCallback(
    (id, key) => dispatch({type: "TOGGLE_PIXELATION_CELL", id, key}),
    []
  );
  const cyclePixelationColor = useCallback(
    (id, key) => dispatch({type: "CYCLE_PIXELATION_COLOR", id, key}),
    []
  );
  const setPixelationPalette = useCallback(
    (source, palette) => dispatch({type: "SET_PIXELATION_PALETTE", source, palette}),
    []
  );
  const resetPixelationPalettes = useCallback(
    () => dispatch({type: "RESET_PIXELATION_PALETTES"}),
    []
  );
  const editPixelationSizeOverride = useCallback(
    (id, value) => dispatch({type: "SET_PIXELATION_SIZE_OVERRIDE", id, value}),
    []
  );
  const editPixelationCoverageOverride = useCallback(
    (id, value) => dispatch({type: "SET_PIXELATION_COVERAGE_OVERRIDE", id, value}),
    []
  );
  const cyclePixelationDirection = useCallback(
    (id) => dispatch({type: "CYCLE_PIXELATION_DIRECTION", id}),
    []
  );
  const resetPixelationTemplate = useCallback(
    (id) => dispatch({type: "RESET_PIXELATION_TEMPLATE", id}),
    []
  );
  const setPixelationCellShape = useCallback(
    (id, key, corners) => dispatch({type: "SET_PIXELATION_CELL_SHAPE", id, key, corners}),
    []
  );
  const resetPixelationShapes = useCallback(() => dispatch({type: "RESET_PIXELATION_SHAPES"}), []);
  const addPixelationMaskStroke = useCallback(
    (id, stroke) => dispatch({type: "ADD_PIXELATION_MASK_STROKE", id, stroke}),
    []
  );
  const undoPixelationMaskStroke = useCallback(
    (id) => dispatch({type: "UNDO_PIXELATION_MASK_STROKE", id}),
    []
  );
  const fillPixelationMask = useCallback(
    (id, value) => dispatch({type: "FILL_PIXELATION_MASK", id, value}),
    []
  );
  const resetPixelationMasks = useCallback(() => dispatch({type: "RESET_PIXELATION_MASKS"}), []);
  const clearSceneSet = useCallback(
    () => dispatch({type: "SET_FIELD", field: "sceneSet", value: null}),
    []
  );

  // Where this selection reads and writes its data. Air carries one offering
  // into every channel that lists it, so its templates, backgrounds and
  // default scenes stay under its home scope while `channel` and `type` keep
  // naming the entry the user came in through. See src/data/offering.ts.
  //
  // The objects carry the home scope's own labels, because that is what the
  // data layer shows: a saved Air scene is a release scene wherever it was
  // made. Consumers that only key a fetch read the two strings.
  const scope = useMemo(() => {
    const {channel: channelValue, type: typeValue} = homeScope(
      state.channel?.value,
      state.type?.value,
      state.product?.value
    );
    if (channelValue === state.channel?.value && typeValue === state.type?.value) {
      return {channel: state.channel, type: state.type};
    }
    const channelObj = findChannelObj(channelValue);
    const typeObj = CONTENT_TYPES.find((t) => t.value === typeValue);
    if (!channelObj || !typeObj) {
      throw new Error(
        `Home scope "${channelValue}/${typeValue}" names no channel or content type — ` +
          `see HOME_SCOPE_BY_PRODUCT in src/data/offering.ts.`
      );
    }
    return {
      channel: {label: channelObj.label, value: channelObj.value},
      type: {label: typeObj.label, value: typeObj.value},
    };
  }, [state.channel, state.type, state.product]);

  const value = useMemo(
    () => ({
      ...state,
      ...wrappers,
      dataChannel: scope.channel?.value ?? "",
      dataType: scope.type?.value ?? "",
      dataChannelObj: scope.channel,
      dataTypeObj: scope.type,
      reset,
      loadScene,
      loadPixelation,
      loadDefaultSceneOptions,
      addVisible,
      resetVisible,
      editVisible,
      editExtraTemplates,
      addLocked,
      removeLocked,
      editLocked,
      editIsBgToolsExpanded,
      editVideoExportCounter,
      toggleAnimationPaused,
      editPerProductBg,
      regenerateGradients,
      registerGenerativeFrameReader,
      getGenerativeFrame,
      regeneratePixelation,
      loadGdnColorVariants,
      cycleGdnColor,
      clearGdnColor,
      togglePixelationCell,
      cyclePixelationColor,
      setPixelationPalette,
      resetPixelationPalettes,
      editPixelationSizeOverride,
      editPixelationCoverageOverride,
      cyclePixelationDirection,
      resetPixelationTemplate,
      setPixelationCellShape,
      resetPixelationShapes,
      addPixelationMaskStroke,
      undoPixelationMaskStroke,
      fillPixelationMask,
      resetPixelationMasks,
      clearSceneSet,
    }),
    [
      state,
      wrappers,
      scope,
      reset,
      loadScene,
      loadPixelation,
      loadDefaultSceneOptions,
      addVisible,
      resetVisible,
      editVisible,
      editExtraTemplates,
      addLocked,
      removeLocked,
      editLocked,
      editIsBgToolsExpanded,
      editVideoExportCounter,
      toggleAnimationPaused,
      editPerProductBg,
      regenerateGradients,
      registerGenerativeFrameReader,
      getGenerativeFrame,
      regeneratePixelation,
      loadGdnColorVariants,
      cycleGdnColor,
      clearGdnColor,
      togglePixelationCell,
      cyclePixelationColor,
      setPixelationPalette,
      resetPixelationPalettes,
      editPixelationSizeOverride,
      editPixelationCoverageOverride,
      cyclePixelationDirection,
      resetPixelationTemplate,
      setPixelationCellShape,
      resetPixelationShapes,
      addPixelationMaskStroke,
      undoPixelationMaskStroke,
      fillPixelationMask,
      resetPixelationMasks,
      clearSceneSet,
    ]
  );

  return <Setting.Provider value={value}>{children}</Setting.Provider>;
};

export function useSetting() {
  const context = useContext(Setting);
  if (context === undefined) {
    throw new Error("Setting.Context must be used within the Setting.Provider");
  }
  return context;
}
